<template>
  <div class="table-wrapper">
    <!-- Toolbar Dialog -->
    <v-container fluid grid-list-md class="table-view-header">
      <v-layout row wrap align-center fill-height>
        <v-flex xs12 sm12 md8 class="title">
          <v-layout row wrap align-center>
            <v-flex md4 lg3
              :class="{
                'pb-2 text-no-wrap': smAndDown
              }"
              @click="$store.commit('setDrawer')"
            >
              <i 
                class="far fa-bars pr-1"
                v-if="smAndDown"
              ></i>
              {{ pageTitle }}
            </v-flex>
            <slot name="date-filter"></slot>
          </v-layout>
        </v-flex>
        <v-flex xs12 sm12 md4
          :class="{
            'px-0': smAndDown
          }"
        >
          <v-layout 
            row 
            wrap 
            :justify-end="!smAndDown"
          >
            <template v-if="hideForm">
              <template v-for="(button, i) in buttons">
                <v-btn
                  v-if="!button.type || button.type == 'button'"
                  color="primary"
                  dark
                  depressed
                  small
                  @click="button.func.call()"
                  :key="i"
                >
                  <i :class="!button.icon ? 'fal fa-plus' : button.icon" />
                  {{ button.text }}
                </v-btn>
              </template>
            </template>
            <v-dialog
              v-if="!hideForm"
              fullscreen
              scrollable
              hide-overlay
              v-model="tableDialog"
              content-class="dialog-custom"
              transition="false"
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  color="primary"
                  dark
                  depressed
                  small
                  v-on="on"
                  v-if="!buttons.length"
                  @click="$emit('addClicked')"
                >
                  <i class="fal fa-plus" />
                  <translate>Add</translate>
                  &nbsp;{{$gettext(title)}}
                </v-btn>

                <template v-for="(button, i) in buttons">
                  <v-btn
                    v-if="!button.type || button.type == 'button'"
                    color="primary"
                    dark
                    depressed
                    small
                    @click="button.func.call()"
                    :key="i"
                    v-on="(typeof(button.canOpen) == 'undefined' || button.canOpen ? on : null)"
                  >
                    <i :class="!button.icon ? 'fal fa-plus' : button.icon" />
                    {{ button.text }}
                  </v-btn>
                  <span :key="`select-${i}`" class="form-group-custom">
                    <pos-select
                      v-if="button.type && button.type == 'select'"
                      v-model="button.form[button.fieldName]"
                      v-validate="button.rules"
                      :items="button.items"
                      :name="button.fieldName"
                      :label="button.placeholder"
                      :error="errors.collect(button.fieldName)"
                    />
                  </span>
                </template>
              </template>
              <v-card>
                <v-card-title v-if="!hideFormTitle" class="text-sm-center">
                  <span class="title">{{ formTitle }}</span>
                </v-card-title>
                <v-card-text ref="formDialog">
                  <v-container fluid grid-list-md pa-0>
                    <slot name="form-dialog"></slot>
                  </v-container>
                </v-card-text>
                <v-card-actions>
                  <v-btn color="primary" dark depressed small @click="handleSave">
                    <i class="fal fa-save" />
                    {{ $gettext('Save') }}
                  </v-btn>
                  <v-btn
                    depressed
                    small
                    @click="handleClose"
                    class="btn-custom--grey"
                  >{{ $gettext('Cancel') }}</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-container>
    <slot name="form-filter"></slot>

    <!-- Filters -->
    <v-container fluid grid-list-md class="table-view-filters" v-if="!noTable && !noFilterTable">
      <v-layout row wrap>
        <v-flex v-if="!hideSearchByName" sm12 md3>
          <v-layout row wrap class="form-group-custom pr-2">
            <v-flex xs12 class="form-group-header">{{ $gettext('Search by name') }}</v-flex>
            <v-flex xs12>
              <v-text-field solo v-model="filterByName" :label="$gettext('Type in keyword ...')">
                <template v-slot:prepend-inner>
                  <i class="fal fa-search" />
                </template>
              </v-text-field>
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex
          sm12
          md2
          v-if="activeFilters.length > 0"
          v-for="(filter, index) in activeFilters"
          :key="index"
        >
          <v-layout row wrap class="form-group-custom pr-2">
            <v-flex xs12 class="form-group-header">{{ filter.label }}</v-flex>
            <v-flex xs12>
              <v-text-field
                v-if="filter.type === 'input-cardnumber'"
                solo
                v-model="filterByCardNumber"
                :label="$gettext('Type card number ...')"
              >
                <template v-slot:prepend-inner>
                  <i class="fal fa-search" />
                </template>
              </v-text-field>
              <v-text-field
                v-if="filter.type === 'input-phone'"
                solo
                v-model="filterByPhone"
                :label="$gettext('Type phone ...')"
              >
                <template v-slot:prepend-inner>
                  <i class="fal fa-search" />
                </template>
              </v-text-field>
              <pos-select v-if="!filter.type" v-model="filter.value" :items="filter.options" />
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex
          sm12
          md3
          ml-auto
          align-self-end
          row
          class="text-sm-right"
          v-if="isCalendarView !== undefined"
        >
          <v-btn v-if="isCalendarView" flat ml-auto @click="$emit('setCalendarView', false)">
            <i class="fal fa-list mr-2" />
            {{ $gettext('List') }}
          </v-btn>
          <v-btn v-else flat @click="$emit('setCalendarView', true)" class="mr-0">
            <i class="fal fa-calendar-alt mr-2" />
            {{ $gettext('Calendar') }}
          </v-btn>
        </v-flex>
      </v-layout>
    </v-container>

    <!-- DataTable -->
    <v-data-table
      :headers="headers"
      :items="filteredItems"
      :pagination.sync="pagination"
      :item-key="itemKey"
      hide-actions
      v-if="!noTable && !isCalendarView"
      id="table-export-excel"
    >
      <template slot="headers" slot-scope="props">
        <tr>
          <th
            v-for="(header, index) in props.headers"
            :key="index"
            :class="[
              'text-xs-left',
              header.checkall ? 'pt-5': '',
              header.sortable != false
                ? [
                  'column sortable',
                  pagination.descending ? 'desc' : 'asc',
                  header.value === pagination.sortBy ? 'active' : ''
                  ]
                : 'column'
            ]"
            @click="header.sortable != false ? changeSort(header.value) : ''"
          >
            <v-checkbox
              class="check-all"
              v-if="header.checkall && false"
              @click="checkAll(selectAll)"
            ></v-checkbox>
            <span :class="{'mt-5 d-block': header.checkall}">{{ header.text }}</span>
            <v-icon small v-if="header.sortable != false">arrow_upward</v-icon>
          </th>
        </tr>
      </template>

      <template #items="props">
        <slot name="displayed-items" :childProps="props"></slot>
        <td v-if="!hideAction">
          <v-btn
            v-if="showCopyItem"
            @click="handleCopy(props.item)"
            :title="buttonTitle(props.item, $gettext('copy'), checkEditItem)"
            :disabled="editButtonDisable(props.item)"
            depressed
            icon
            flat
            small
            class="grey--text mx-0"
          >
            <i class="far fa-clone" />
          </v-btn>
          <v-btn
            @click="handleEdit(props.item)"
            v-if="!hideActionEdit"
            :title="buttonTitle(props.item, $gettext('edit'), checkEditItem)"
            :disabled="editButtonDisable(props.item)"
            depressed
            icon
            flat
            small
            class="grey--text mx-0"
          >
            <i class="fas fa-pencil" />
          </v-btn>
          <v-btn
            v-if="!hideActionDelete"
            @click="handleDelete(props.item)"
            :title="buttonTitle(props.item, $gettext('delete'), checkDeleteItem)"
            :disabled="deleteButtonDisable(props.item)"
            depressed
            icon
            flat
            small
            class="grey--text mx-0"
          >
            <i class="far fa-trash-alt" />
          </v-btn>
        </td>
      </template>
      <template v-slot:no-data>
        <p class="mt-3">{{ $gettext('No results found!') }}</p>
      </template>
    </v-data-table>

    <slot v-if="!noTable && isCalendarView" name="calendar-view"></slot>

    <!-- Pagination -->
    <div
      class="form-group-custom py-3 table-pagination"
      v-if="!hidePaging && !noTable && !isCalendarView"
    >
      <span v-translate>Show</span>
      <pos-select
        :clearable="false"
        :items="pagination.rowsPerPageItems"
        v-model="pagination.rowsPerPage"
      />
      <span>{{ filteredItems.length }}</span>&nbsp;
      <span v-translate>items total</span>
      <v-pagination
        v-model="pagination.page"
        :length="pages"
        :total-visible="7"
        prev-icon="fal fa-chevron-left"
        next-icon="fal fa-chevron-right"
      />
    </div>

    <!-- Confirm Modal -->
    <confirm ref="confirm" />
  </div>
</template>

<script>
import { 
  SET_TABLE_DIALOG, 
  SET_ACTIVE_ELEMENT
} from "@/store/mutation-types";
import Confirm from "@/components/Confirm.vue";
import XLSX from "xlsx";

export default {
  name: "TableView",
  components: {
    Confirm
  },
  inject: ["$parentValidator"],
  props: {
    title: String,
    filters: { type: Array, default: () => [] },
    headers: { type: Array, default: () => [] },
    items: { type: Array, default: () => [] },
    buttons: { type: Array, default: () => [] },
    forms: { type: Array, default: () => [] },
    showCopyItem: { type: Boolean, default: false },
    checkEditItem: { type: String, default: "" },
    checkDeleteItem: { type: String, default: "" },
    hideAction: { type: Boolean, default: false },
    hideActionDelete: { type: Boolean, default: false },
    hideActionEdit: { type: Boolean, default: false },
    hideForm: { type: Boolean, default: false },
    hideFormTitle: { type: Boolean, default: false },
    hideSearchByName: { type: Boolean, default: false },
    itemKey: { type: String, default: "id" },
    sortBy: { type: String, default: "id" },
    sortDesc: { type: Boolean, default: true },
    rowsPerPage: { type: Number, default: 25 },
    hidePaging: { type: Boolean, default: false },
    noTable: { type: Boolean, default: false },
    isCalendarView: { type: Boolean, default: undefined },
    noFilterTable: { type: Boolean, default: false }
  },
  data() {
    return {
      filterByName: "",
      filterByCardNumber: "",
      filterByPhone: "",
      isEditing: false,
      pagination: {
        descending: this.sortDesc,
        sortBy: this.sortBy,
        rowsPerPageItems: [25, 50, 100, { name: "All", id: -1 }],
        rowsPerPage: this.rowsPerPage
      },
      pageTitle: this.$router.currentRoute.meta.title,
      activeFilters: [],
      selectAll: false,
      alarmClock: 0
    };
  },
  computed: {
    formTitle() {
      const action = this.isEditing
        ? this.$gettext("Update")
        : this.$gettext("Add");
      return `${action} ${this.$gettext(this.title)}`;
    },
    filteredItems() {
      const alarmClock = this.alarmClock;
      let items = Object.assign([], this.items);
      if (this.filterByName) {
        const search = this.filterByName.toLowerCase();
        items = items.filter(item => {
          if (item.name) {
            return item.name.toLowerCase().includes(search);
          } else if (item.preset) {
            return item.preset.toLowerCase().includes(search);
          }
        });
      }
      if (this.filterByCardNumber) {
        const search = this.filterByCardNumber.toLowerCase();
        items = items.filter(item =>
          (item.cardNumber || "").toLowerCase().includes(search)
        );
      }
      if (this.filterByPhone) {
        const search = this.filterByPhone.toLowerCase();
        items = items.filter(item => item.phone.toLowerCase().includes(search));
      }

      this.activeFilters.forEach(filter => {
        if (filter.value || filter.value == 0) {
          if (filter.mapper && filter.mapper[filter.value]) {
            if (filter.mapper[filter.value] == "undefined") {
              items = items.filter(
                item => typeof item[filter.field] == "undefined"
              );
            } else {
              items = items.filter(
                item =>
                  item[filter.field] &&
                  item[filter.field] === filter.mapper[filter.value]
              );
            }
          } else {
            items = items.filter(
              item =>
                typeof (item[filter.field] != "undefined") &&
                item[filter.field] === filter.value
            );
          }
        }
      });
      return items;
    },
    tableDialog: {
      get() {
        return this.$store.state.toggle.tableDialog;
      },
      set(value) {
        this.$store.commit(SET_TABLE_DIALOG, value);
      }
    },
    pages() {
      return this.pagination.rowsPerPage
        ? Math.ceil(this.filteredItems.length / this.pagination.rowsPerPage)
        : 0;
    },
  },
  watch: {
    tableDialog(val) {
      if (!val) {
        this.handleClose();
        this.$refs.formDialog.scrollTop = 0;
      }
    },
    filters: {
      handler(value) {
        this.$set(this, "activeFilters", this.filters);
      },
      deep: true
    },
    pagination: {
      handler(value) {
        if (!value.sortBy && this.sortBy != "id") {
          this.pagination.sortBy = this.sortBy;
          this.pagination.descending = this.sortDesc;
        }
      },
      deep: true
    }
  },
  methods: {
    forceReloadFilters(filters) {
      this.$set(this, "activeFilters", filters);
    },
    forceReloadItems() {
      this.alarmClock ++;
    },
    handleCopy(item) {
      this.$emit("onCopy", item);
      this.$store.commit(SET_TABLE_DIALOG, true);
    },
    handleEdit(item) {
      this.$emit("onEdit", item);
      this.isEditing = true;
      this.$store.commit(SET_TABLE_DIALOG, true);
    },
    handleClose() {
      if (this.tableDialog) {
        this.$store.commit(SET_TABLE_DIALOG, false);
      }
      this.$parentValidator.reset();
      this.forms.forEach(form => {
        form.$validator.reset();
      });
      this.isEditing = false;
      this.$emit("onCancel");
    },
    async handleSave(evt) {
      const validatePromises = [];
      this.forms.forEach(form => {
        validatePromises.push(form.validate());
      });
      const isValid = await Promise.all(validatePromises).then(rs =>
        rs.every(r => r)
      );

      if (isValid === false) {
        this.$refs.formDialog.scrollTop = 0;
        return;
      }
      this.$store.commit(SET_ACTIVE_ELEMENT, evt.target);
      this.$emit("onSave");
    },
    async handleDelete(item) {
      const isConfirmed = await this.$refs.confirm.open(
        "Delete",
        "Are you sure you want to delete this item?",
        { color: "error" }
      );
      if (isConfirmed) {
        this.$emit("onDelete", item);
      }
    },
    buttonTitle(item, title, checkAction) {
      if (checkAction == "") return title;
      const canDoAction = this.$store.getters[checkAction](item);
      if (canDoAction === true) return title;
      return canDoAction;
    },
    editButtonDisable(item) {
      if (this.checkEditItem == "") return false;
      const canEditItem = this.$store.getters[this.checkEditItem](item);
      return !(canEditItem === true);
    },
    deleteButtonDisable(item) {
      if (this.checkDeleteItem == "") return false;
      const canDeleteItem = this.$store.getters[this.checkDeleteItem](item);
      return !(canDeleteItem === true);
    },
    changeSort(field) {
      this.pagination.descending = !this.pagination.descending;
      this.pagination.sortBy = field;
    },
    checkAll(selectAll) {
      this.$emit("checkAll", {
        selected: !selectAll,
        data: this.filteredItems
      });
      this.selectAll = !selectAll;
    },
  }
};
</script>
<style lang="scss">
  .primary {
    color: #000 !important;
    font-weight: 700;
  }

</style>
